import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError, API } from "@/lib/api";
import { formatDateShort } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, FileText, Image as ImageIcon, Download, Trash2, Loader2, Paperclip, ExternalLink } from "lucide-react";

const iconFor = (ct) => {
  if (!ct) return FileText;
  if (ct.startsWith("image/")) return ImageIcon;
  return FileText;
};

const humanSize = (n) => {
  if (!n) return "-";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export default function UnitAttachments({ unit, project, subcontract, canEdit = true, defaultLabel }) {
  const scope = subcontract ? "subcontract" : project ? "project" : "unit";
  const ownerId = subcontract ? subcontract.id : project ? project.id : unit?.id;
  const listUrl = subcontract
    ? `/subcontracts/${subcontract.id}/attachments`
    : project
      ? `/projects/${project.id}/attachments`
      : `/units/${unit.id}/attachments`;
  const uploadUrl = listUrl;
  const emptyLabel =
    defaultLabel ||
    (subcontract ? "İmzalı Sözleşme" : project ? "Proje Belgesi" : "Sözleşme Taraması");

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState(emptyLabel);
  const inputRef = useRef(null);

  const load = async () => {
    if (!ownerId) return;
    setLoading(true);
    try {
      const { data } = await api.get(listUrl);
      setFiles(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ownerId]);

  const onSelect = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploading(true);
    for (const f of list) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("label", label || emptyLabel);
      try {
        await api.post(uploadUrl, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch (err) {
        toast.error(`${f.name}: ${formatApiError(err.response?.data?.detail)}`);
      }
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
    toast.success("Yüklendi");
    load();
  };

  const openFile = (f) => {
    // Same-origin GET → cookie auth sent automatically. Sync window.open bypasses popup blocker.
    window.open(`${API}/attachments/${f.id}/download`, "_blank", "noopener");
  };

  const downloadFile = async (f) => {
    try {
      const { data } = await api.get(`/attachments/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.original_filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (f) => {
    if (!window.confirm(`"${f.original_filename}" dosyasını silmek istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`/attachments/${f.id}`);
      toast.success("Dosya silindi");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <section className="p-4 rounded-md border border-border" data-testid="unit-attachments">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Paperclip className="w-4 h-4 text-brand" />
          <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
            Ekli Belgeler ({files.length})
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Belge etiketi"
              className="h-8 w-48 text-xs"
              data-testid="attachment-label"
            />
            <input
              ref={inputRef}
              type="file"
              multiple
              accept="application/pdf,image/jpeg,image/png,image/webp"
              onChange={onSelect}
              className="hidden"
              data-testid="attachment-input"
            />
            <Button
              size="sm"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid="attachment-upload-btn"
            >
              {uploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
              Belge Yükle
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-xs text-muted-foreground py-4 text-center">Yükleniyor…</div>
      ) : files.length === 0 ? (
        <div className="text-xs text-muted-foreground p-6 text-center border border-dashed border-border rounded-md">
          {scope === "subcontract"
            ? "Henüz belge yok. İmzalı taşeron sözleşmesi taramasını, ek anlaşmaları, kaşeli faturaları PDF/JPG/PNG olarak yükleyebilirsiniz."
            : scope === "project"
              ? "Henüz belge yok. Sözleşme, ruhsat, mimari plan, tapu vb. proje belgelerini PDF/JPG/PNG olarak yükleyebilirsiniz."
              : "Henüz belge yok. Taranmış sözleşme, tapu, kimlik vb. PDF/JPG/PNG belgelerini yükleyebilirsiniz."}
        </div>
      ) : (
        <div className="space-y-2">
          {files.map((f) => {
            const Icon = iconFor(f.content_type);
            return (
              <div
                key={f.id}
                data-testid={`attachment-row-${f.id}`}
                className="flex items-center gap-3 p-3 border border-border rounded-md hover:bg-secondary/40 transition-colors"
              >
                <div className="w-9 h-9 rounded bg-secondary flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-brand" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{f.original_filename}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
                    {f.label} · {humanSize(f.size)} · {formatDateShort(f.uploaded_at)}
                    {f.uploaded_by_name ? ` · ${f.uploaded_by_name}` : ""}
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => openFile(f)} data-testid={`attachment-open-${f.id}`} className="h-8 w-8 p-0" title="Görüntüle">
                  <ExternalLink className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => downloadFile(f)} className="h-8 w-8 p-0" title="İndir">
                  <Download className="w-3 h-3" />
                </Button>
                {canEdit && (
                  <Button size="sm" variant="ghost" onClick={() => remove(f)} data-testid={`attachment-delete-${f.id}`} className="h-8 w-8 p-0 text-destructive" title="Sil">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
